import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { CustomSelect } from '@/components/form/CustomSelect';
import { MultiSelect } from '@/components/form/MultiSelect';
import type { PermissionGroup } from '@/api/roles';
import { GROUP_ICONS } from '@/pages/roles/groupIcons';
import type { AdminUserOptions } from '@/api/adminUsers';
import {
  validateAdminPassword,
  PASSWORD_MIN_LENGTH,
  type AdminFormState,
} from './adminFormState';

/**
 * Блоки полей карточки администратора — общие для правки существующего
 * (вкладки в AdminUserForm) и мастера создания (шаги в AdminCreateWizard),
 * чтобы разметка полей жила в одном месте.
 */
export interface FieldsProps {
  state: AdminFormState;
  onChange: (next: AdminFormState) => void;
  options: AdminUserOptions | null;
  disabled: boolean;
  /** Ключи полей, подсвечиваемых как незаполненные. */
  fieldErrors?: string[];
}

export function ProfileFields({ state, onChange, options, disabled, fieldErrors = [] }: FieldsProps) {
  const set = <K extends keyof AdminFormState>(key: K, value: AdminFormState[K]) =>
    onChange({ ...state, [key]: value });
  const invalid = (field: string) => (fieldErrors.includes(field) ? ' edit-field--invalid' : '');

  return (
    <div className="edit-fields">
      <label className={`edit-field${invalid('name')}`}>
        <span>Название *</span>
        <input type="text" value={state.name} onChange={(e) => set('name', e.target.value)} disabled={disabled} />
      </label>

      <label className="edit-field">
        <span>Фамилия</span>
        <input
          type="text"
          value={state.lastName}
          onChange={(e) => set('lastName', e.target.value)}
          disabled={disabled}
        />
      </label>

      <label className="edit-field">
        <span>Имя</span>
        <input
          type="text"
          value={state.firstName}
          onChange={(e) => set('firstName', e.target.value)}
          disabled={disabled}
        />
      </label>

      <label className="edit-field">
        <span>Отчество</span>
        <input
          type="text"
          value={state.surname}
          onChange={(e) => set('surname', e.target.value)}
          disabled={disabled}
        />
      </label>

      <label className={`edit-field${invalid('email')}`}>
        <span>Электронная почта *</span>
        <input type="email" value={state.email} onChange={(e) => set('email', e.target.value)} disabled={disabled} />
      </label>

      <div className="edit-field">
        <span>Статус кабинета</span>
        <CustomSelect
          value={state.status}
          options={options?.statuses ?? []}
          disabled={disabled || !options}
          onChange={(value) => set('status', value)}
        />
      </div>
    </div>
  );
}

/** login — только показ: он заводится на сервере авторизации (у нового администратора его ещё нет). */
export function SsoFields({
  state,
  onChange,
  options,
  disabled,
  login,
}: FieldsProps & { login: string | null }) {
  const passwordError = validateAdminPassword(state);

  return (
    <div className="edit-fields">
      {login !== null && (
        <label className="edit-field">
          <span>Единый логин (на сервере авторизации)</span>
          <input type="text" value={login} readOnly disabled />
          <span className="edit-field__hint">Логин задаётся на сервере авторизации и здесь не меняется.</span>
        </label>
      )}

      {/* Короткий пароль подсвечиваем сразу при вводе, а не ответом сервера. */}
      <label className={`edit-field${passwordError ? ' edit-field--invalid' : ''}`}>
        <span>Пароль</span>
        <input
          type="password"
          value={state.password}
          autoComplete="new-password"
          onChange={(e) => onChange({ ...state, password: e.target.value })}
          disabled={disabled}
        />
        {passwordError ? (
          <span className="edit-field__hint edit-field__hint--error">{passwordError}</span>
        ) : (
          <span className="edit-field__hint">
            {login === null
              ? `Оставьте пустым, чтобы пароль сгенерировался автоматически. Не короче ${PASSWORD_MIN_LENGTH} символов.`
              : `Оставьте пустым, чтобы не менять пароль. Не короче ${PASSWORD_MIN_LENGTH} символов.`}
          </span>
        )}
      </label>

      <div className="edit-field edit-field--full">
        <span>Синхронизация с серверами</span>
        <CustomSelect
          value={state.mt1Server}
          options={[{ value: '', label: 'Не синхронизировать' }, ...(options?.mt1Servers ?? [])]}
          disabled={disabled || !options}
          onChange={(value) => onChange({ ...state, mt1Server: value })}
        />
        <span className="edit-field__hint">Выберите сервер MT1 для синхронизации.</span>
      </div>
    </div>
  );
}

export function RolesFields({
  state,
  onChange,
  options,
  disabled,
  fieldErrors = [],
  catalog,
}: FieldsProps & { catalog: PermissionGroup[] | null }) {
  const roleOptions = (options?.roles ?? []).map((r) => ({ id: Number(r.value), name: r.label }));

  const togglePermission = (slug: string) =>
    onChange({
      ...state,
      permissions: state.permissions.includes(slug)
        ? state.permissions.filter((s) => s !== slug)
        : [...state.permissions, slug],
    });

  return (
    <>
      <div className="edit-fields">
        {/* Роли — выпадающим списком с чипами, как «Кураторы» у клиента. */}
        <div className={`edit-field edit-field--full${fieldErrors.includes('roleIds') ? ' edit-field--invalid' : ''}`}>
          <span>Роли *</span>
          {options === null ? (
            <div className="panel-loading">
              <Spinner />
            </div>
          ) : (
            <MultiSelect
              values={state.roleIds}
              options={roleOptions}
              disabled={disabled}
              onChange={(roleIds) => onChange({ ...state, roleIds })}
              label="Роли"
              emptyText="Нет доступных ролей"
              selectAll
            />
          )}
          <span className="edit-field__hint">Права ролей суммируются.</span>
        </div>
      </div>

      <div className="permissions-block">
        <h2 className="detail__subtitle">Персональные разрешения</h2>
        <p className="edit-field__hint">Выдаются сверх прав, полученных от ролей.</p>

        {catalog === null ? (
          <div className="panel-loading">
            <Spinner />
          </div>
        ) : (
          <div className="permissions">
            {catalog.map((group) => {
              const slugs = group.permissions.map((p) => p.slug);
              const checkedCount = slugs.filter((s) => state.permissions.includes(s)).length;

              return (
                <section
                  className={`permissions__group${checkedCount > 0 ? ' permissions__group--active' : ''}`}
                  key={group.title}
                >
                  <header className="permissions__head">
                    <span className="permissions__icon">
                      <Icon name={GROUP_ICONS[group.title] ?? 'lock'} size={16} />
                    </span>
                    <span className="permissions__title">{group.title}</span>
                    <span className="permissions__count">
                      {checkedCount} из {slugs.length}
                    </span>
                  </header>

                  <ul className="permissions__list">
                    {group.permissions.map((permission) => {
                      const checked = state.permissions.includes(permission.slug);
                      return (
                        <li key={permission.slug}>
                          <label className={`permissions__item${checked ? ' permissions__item--on' : ''}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={disabled}
                              onChange={() => togglePermission(permission.slug)}
                            />
                            <span className="permissions__label">{permission.description}</span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
